import React from "react";
import { isEmpty as _isEmpty } from "./utils";
import * as _ from "lodash";
import { diffLines, diffWordsWithSpace } from "diff";
import { stringify } from "postcss";

export const _epsilon = 0.0001;

export function isTrue(value: boolean, message?: string): void {
  if (!value) {
    throw new AssertError(formatMessage(message, "Assert isTrue failed"));
  }
}
export function isFalse(value: boolean, message?: string): void {
  if (value) {
    throw new AssertError(formatMessage(message, "Assert isFalse failed"));
  }
}
export function isNull(value: any, message?: string): void {
  if (value != null) {
    throw new AssertError(formatMessage(message, "Assert isNull failed"));
  }
}
export function isNotNull(value: any, message?: string): void {
  if (value == null) {
    throw new AssertError(formatMessage(message, "Assert isNotNull failed : " + value));
  }
}
export function isEmpty(value: any, message?: string): void {
  if (!_isEmpty(value)) {
    throw new AssertError(formatMessage(message, "Assert isEmpty failed"));
  }
}
export function notIsEmpty(value: any, message?: string): void {
  if (_isEmpty(value)) {
    throw new AssertError(formatMessage(message, "Assert notIsEmpty failed"));
  }
}
export function isNaN(value: number, message?: string): void {
  if (!Number.isNaN(value)) {
    throw new AssertError(formatMessage(message, "Assert isNaN failed"));
  }
}
export function isNotNaN(value: number, message?: string): void {
  if (Number.isNaN(value)) {
    throw new AssertError(formatMessage(message, "Assert isNotNaN failed"));
  }
}
export function equals(
  expected: any,
  actualValue: any,
  message?: string,
  epsilon = _epsilon,
): void {
  if (!equalEpsilon(expected, actualValue, epsilon)) {
    throw new AssertError(
      formatMessage(
        message,
        "Assert equals failed: " +
          _format(expected, epsilon) +
          " != " +
          _format(actualValue, epsilon),
      ),
    );
  }
}
export function notEquals(
  expected: any,
  actualValue: any,
  message?: string,
  epsilon = _epsilon,
): void {
  if (equalEpsilon(expected, actualValue, epsilon)) {
    throw new AssertError(
      formatMessage(
        message,
        "Assert notEquals failed: " +
          _format(expected, epsilon) +
          " == " +
          _format(actualValue, epsilon),
      ),
    );
  }
}
export function same(expected: any, actualValue: any, message?: string): void {
  if (expected !== actualValue) {
    throw new AssertError(
      formatMessage(
        message,
        "Assert same failed: " + _format(expected) + " !== " + _format(actualValue),
      ),
    );
  }
}
export function notSame(expected: any, actualValue: any, message?: string): void {
  if (expected === actualValue) {
    throw new AssertError(
      formatMessage(
        message,
        "Assert notSame failed: " + _format(expected) + " === " + _format(actualValue),
      ),
    );
  }
}
export function contains(expected: object, actualValue: object[], message?: string): void {
  const found = actualValue.some((item) => _.isEqual(item, expected));

  if (!found) {
    throw new AssertError(
      formatMessage(
        message,
        "Assert contains failed: " +
          JSON.stringify(actualValue) +
          " does not contain " +
          JSON.stringify(expected),
      ),
    );
  }
}

/**
 * Speziell bei serialisierten Objekten scheint _.isEqualWith manchmal von diffLines abweichende
 * Ergebnisse zu erzielen. Der Umstand, dass der Deep‐Equal‑Vergleich immer noch fehlschlägt –
 * obwohl die JSON‑Diff-Ausgabe identisch ist – deutet darauf hin, dass die zu vergleichenden Objekte
 * in ihrer „rohen“ Struktur (also als JavaScript‑Objekte) Unterschiede aufweisen,
 * die JSON.stringify (bzw. toJSON‑Aufrufe) nicht mehr erfasst.
 * Ein häufiger Grund dafür ist, dass Objekte mit eigenen toJSON‑Methoden vor dem Stringify in ein
 * anderes Format transformiert werden, Prototyp-Informationen oder andere interne Eigenschaften
 * (z.B. nicht-enumerable Felder) im Deep‑Equal-Vergleich (bspw. mit Lodash’s _.isEqualWith)
 * berücksichtigt werden, auch wenn sie im JSON‑Output nicht auftauchen.
 */
export function deepEqualsStringified(
  expectedJson: any,
  actualValueJson: any,
  message?: string,
  epsilon = _epsilon,
): void {
  const expectedClean = JSON.parse(JSON.stringify(expectedJson));
  const actualClean = JSON.parse(JSON.stringify(actualValueJson));
  return deepEquals(expectedClean, actualClean, message, epsilon);
}
export function deepEquals(
  expected: any,
  actualValue: any,
  message?: string,
  epsilon = _epsilon,
): void {
  if (!equalEpsilonDeep(expected, actualValue, epsilon)) {
    const error = new AssertError(formatMessage(message, " Assert deepEquals failed"));
    try {
      const values = _roundValues({ expected, actualValue }, epsilon);
      // diff for console error
      const stringify = (v) => {
        const seen = new WeakSet();
        return JSON.stringify(
          v,
          function (key, value) {
            if (typeof value === "object" && value !== null) {
              if (seen.has(value)) {
                return "[Circular]";
              }
              seen.add(value);
            }
            if (
              Number.isNaN(value) ||
              value === Number.POSITIVE_INFINITY ||
              value === Number.NEGATIVE_INFINITY
            ) {
              return "" + value;
            }
            return value;
          },
          2,
        );
      };
      const expectedJson = stringify(values.expected);
      const actualJson = stringify(values.actualValue);

      const lineCount = (str: string) => (str.match(/\n/g) || "").length + 1;

      const lineCountMax = Math.max(lineCount(expectedJson), lineCount(actualJson));
      if (Math.max(lineCount(expectedJson), lineCount(actualJson)) > 5000) {
        error["_diffReadable"] = () => {
          const expectedLines = expectedJson.split(/\n/);
          const actualLines = actualJson.split(/\n/);
          let i = 0;
          for (; i < lineCountMax; i++) {
            if (expectedLines[i] != actualLines[i]) {
              return (
                <div>
                  acutal:
                  <pre>
                    <code>{actualJson[i]}</code>
                  </pre>
                  expected:
                  <pre>
                    <code>{expectedLines[i]}</code>
                  </pre>
                </div>
              );
            }
          }

          return "json is too big to show differences";
        };
      } else {
        const diff = diffLines(expectedJson, actualJson);

        diff
          .filter((part) => part.removed || part.added)
          .map((part) => (part.added ? "@++" : part.removed ? "@--" : "@@") + " " + part.value);
        console.error("assert.deepEquals failed", values);

        // diff for TestRunner.tsx
        error["_diffReadable"] = () => {
          const max = 3;
          return (
            <div className="diff">
              {diff.map((part, index) => (
                <span key={index} className={part.added ? "green" : part.removed ? "red" : "grey"}>
                  {part.value}
                </span>
              ))}
            </div>
          );
        };
      }
    } catch (e) {
      console.error("assert.deepEquals failed", { expected, actualValue }, e);
    }
    throw error;
  }
}
export function notDeepEquals(
  expected: any,
  actualValue: any,
  message?: string,
  epsilon = _epsilon,
): void {
  if (equalEpsilonDeep(expected, actualValue, epsilon)) {
    console.error("assert.notDeepEquals", { expected, actualValue });
    throw new AssertError(formatMessage(message, "Assert notDeepEquals failed"));
  }
}

export function deepEqualsPicked(
  expected: any,
  actualValue: any,
  message?: string,
  epsilon = _epsilon,
) {
  function pick(a, b) {
    if (b == null) {
      return b;
    }
    if (Array.isArray(a)) {
      if (Array.isArray(b)) {
        return b.map((v, i) => pick(a[i], b[i]));
      }
      return b;
    }
    if (_.isObject(a)) {
      if (_.isObject(b)) {
        return _.fromPairs(Object.keys(a).map((key) => [key, pick(a[key], b[key])]));
      }
      return b;
    }
    return b;
  }
  const actualValuePicked = pick(expected, actualValue);
  return deepEquals(expected, actualValuePicked, message, epsilon);
}

function equalEpsilon(a, b, epsilon = _epsilon) {
  return _equalEpsilon(a, b, epsilon) ?? false;
}

function equalEpsilonDeep(a, b, epsilon = _epsilon) {
  return _.isEqualWith(a, b, (a, b) => _equalEpsilon(a, b, epsilon));
}

function _equalEpsilon(a, b, epsilon: number) {
  if (a == b) {
    return true;
  }
  if (typeof a == "number" && typeof b == "number") {
    return Math.abs(a - b) < epsilon;
  }
  return undefined;
}

function _roundValues(object, epsilon: number, visited = undefined, maxDepth = 10) {
  if (maxDepth == 0) {
    return "@maxDepth";
  }
  if (object == null) {
    return object;
  }
  if (typeof object === "number") {
    const f = Math.round(1 / epsilon);
    return Math.round(object * f) / f;
  }
  if (visited) {
    if (visited.has(object)) {
      return "@circle";
    }
  }
  const v = new Set([...(visited ?? []), object]);
  if (Array.isArray(object)) {
    return object.map((o) => _roundValues(o, epsilon, v, maxDepth - 1));
  }
  if (_.isPlainObject(object)) {
    return _.mapValues(object, (o) => _roundValues(o, epsilon, v, maxDepth - 1));
  }
  return object;
}

function _format(object, epsilon = 0.0000001) {
  if (object == null) {
    return object;
  }
  if (typeof object === "string") {
    return '"' + object + '"';
  }
  if (typeof object === "number") {
    const f = Math.round(1 / epsilon);
    return Math.round(object * f) / f;
  }
  const key = Object.keys(object).find((key) => /_*(key|name|label|id|uid|uuid)_*/i.test(key));
  const type = typeof object[key];
  if (key && /string|number|boolean/.test(type)) {
    if (type === "string") {
      return `{${key}: "${object[key]}",...}`;
    }
    return `{${key}: ${object[key]},...}`;
  }
  return object;
}

export class AssertError extends Error {
  constructor(...args) {
    super(...args);
  }
}

export function formatMessage(message1, message2) {
  return (message1 == null ? "" : message1 + ": ") + message2;
}
